import { pmFetchJson } from "@penmate/react-shared/api/pmFetch";

// Mirrors the v2 contract: POST /api/searches, GET /api/searches/:id, and
// the step-2 top-up PATCH /api/searches/:id.

export type Candidate = {
  name: string;
  firstName: string | null;
  lastName: string | null;
  inmateId: string | null;
  facility: string | null;
  facilityType: string | null;
  custodyStatus: string | null;
  age: string | null;
  nameExact: boolean;
  nameScore: number;
};

export type SearchAttempt = {
  id: number;
  source: string;
  status: "pending" | "running" | "matches" | "empty" | "error" | "unsupported";
  tier: number | null;
  candidates: Candidate[];
  error_category: string | null;
  resolved_facility_address_id: number | null;
  started_at: string | null;
  finished_at: string | null;
};

export type SearchResource = {
  id: number;
  status: "pending" | "running" | "complete" | "failed";
  first_name: string;
  last_name: string;
  state: string;
  resolved_inmate_id: number | null;
  attempts: SearchAttempt[];
  // Wait/release progress riding the payload (the search-queue contract, not
  // the frozen v2 one); validated and consumed only by searchProgress.ts.
  progress?: unknown;
};

export type CreateSearchBody = {
  first_name: string;
  last_name: string;
  state: string;
  inmate_id?: string;
  birthdate?: string;
  known_facility_id?: number;
};

export async function createSearch(body: CreateSearchBody): Promise<SearchResource> {
  const res = await pmFetchJson<{ search: SearchResource }>("/searches", {
    method: "POST",
    body,
  });
  return res.search;
}

// Best-effort top-ups after the search fired; the server never fails the
// search over any of it. Retracting the facility requires the key PRESENT
// and null (an absent key means no change; null retracts the pick and
// un-points resolutions it produced) — this client still always sends the
// key so every call states the full intended facility state. `birthdate` is
// strict YYYY-MM-DD, write-if-parseable — absent or null means no change,
// never a clear; junk and future dates are dropped server-side.
export type UpdateSearchBody = {
  known_facility_id: number | null;
  birthdate?: string;
};

export async function updateSearch(id: number, body: UpdateSearchBody): Promise<SearchResource> {
  const res = await pmFetchJson<{ search: SearchResource }>(`/searches/${id}`, {
    method: "PATCH",
    body,
  });
  return res.search;
}

export async function updateSearchKnownFacility(
  id: number,
  knownFacilityId: number | null,
): Promise<SearchResource> {
  return updateSearch(id, { known_facility_id: knownFacilityId });
}

export async function getSearch(id: number): Promise<SearchResource> {
  const res = await pmFetchJson<{ search: SearchResource }>(`/searches/${id}`);
  return res.search;
}
